/**
 * Modern Bold · Favorites page
 * ─────────────────────────────────────────────────────────────────────────
 * <mb-favorites-page> renders the customer's lists and their saved products.
 * The store (MBFavorites, assets/modern-wishlist.js) owns the data; this file
 * only draws it, so the same lists appear here and behind every card heart.
 *
 * Cards are NOT built here. They come from the Section Rendering API —
 *   {search_url}?type=product&q=id:1 OR id:2&section_id=modern-favorites
 * — which returns real modern-collection-card markup, the same snippet the
 * collection grid uses. Same architecture as assets/modern-recently-viewed.js.
 * One request per set of unseen products, not one per product, and the price,
 * badge and featured-variant rules come from Liquid rather than a second
 * implementation over here.
 *
 * Products are addressed by id, never handle: Shopify creates no redirect when
 * a handle changes, so a handle-keyed favorite dies silently on rename. Items
 * restored from the previous release carry a handle only; they are resolved
 * once through /products/<handle>.js and upgraded in the store.
 *
 * Data attributes:
 *   data-search-url   routes.search_url
 *   data-browse-url   where the empty state sends the customer
 */
if (!customElements.get('mb-favorites-page')) {
  const PAGE_SIZE = 50; // the {% paginate %} ceiling in the section
  const SECTION_ID = 'modern-favorites';

  const DOTS = '<svg class="mb-icon" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false"><circle cx="5" cy="12" r="1.9"/><circle cx="12" cy="12" r="1.9"/><circle cx="19" cy="12" r="1.9"/></svg>';
  const HEART = '<svg class="mb-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>';

  const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /* Rendered cards, keyed by product id and kept for the life of the page.
     An empty string means "asked for, not returned" — an unpublished or
     deleted product — so it is never requested twice or left as a skeleton. */
  const cards = new Map();

  /* Handles already put through the id backfill, successful or not, so a dead
     one cannot spin the render/resolve loop. */
  const resolved = new Set();

  /* The only survivor of the old per-handle fetch layer: it turns a v2
     handle-only item into an id the section render can query by. */
  const backfill = async (items) => {
    await Promise.all(items.map(async ({ handle }) => {
      resolved.add(handle);
      try {
        const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' });
        if (!res.ok) return;
        const product = await res.json();
        if (product?.id) MBFavorites.upgrade({ id: product.id, handle });
      } catch { /* offline — the handle stays unresolved until the next visit */ }
    }));
  };

  class MBFavoritesPage extends HTMLElement {
    #browseUrl = '/collections/all';
    #searchUrl = '/search';
    #pendingRender = false;
    #abort = null;
    // One supply run at a time. Without it a re-render starts a second run
    // that aborts the first, whose failure triggers another re-render — the
    // two ping-pong until the tab dies.
    #busy = false;
    // Where focus should land on the next render. Store mutations re-render
    // before the calling method returns, so the target is set beforehand.
    #nextFocus = null;

    #onUpdate = () => {
      // Holding the render keeps the open picker's anchor alive; the menu tells
      // us when it closes.
      if (document.querySelector('mb-favorites-menu:popover-open')) this.#pendingRender = true;
      else this.#render();
    };

    #onMenuToggle = (event) => {
      if (event.detail.open || !this.#pendingRender) return;
      this.#pendingRender = false;
      const id = event.detail.ref?.id;
      // Focus is handed back here, not by the menu: this render detaches the
      // button the menu was anchored to, so its own restore would find the
      // element gone and drop focus to <body>.
      this.#render(`card:${id}`);
    };

    connectedCallback() {
      if (!window.MBFavorites) return;
      this.#browseUrl = this.dataset.browseUrl || this.#browseUrl;
      this.#searchUrl = this.dataset.searchUrl || this.#searchUrl;

      this.addEventListener('click', this.#onClick);
      document.addEventListener('mb:wishlist-updated', this.#onUpdate);
      document.addEventListener('mb:favorites-menu', this.#onMenuToggle);
      this.#render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
      document.removeEventListener('mb:wishlist-updated', this.#onUpdate);
      document.removeEventListener('mb:favorites-menu', this.#onMenuToggle);
      this.#abort?.abort();
    }

    /* ── Interaction ──────────────────────────────────────────────────── */

    #onClick = (event) => {
      const chip = event.target.closest('[data-list-id]');
      if (chip?.dataset.listId) {
        MBFavorites.setActive(chip.dataset.listId);
        return;
      }
      const control = event.target.closest('[data-fav-action]');
      const action = control?.dataset.favAction;
      if (action === 'new') this.#createList();
      else if (action === 'rename') this.#renameList();
      else if (action === 'delete') this.#deleteList();
      // 'menu' is deliberately absent: the ⋯ binds its own listener in
      // #fillGrid. Delegation from here reached it on desktop but not on
      // touch, while the heart — which owns its listener — worked on both.
    };

    /* Bound per button rather than delegated, and it stops the event dead:
       the card's photo link sits under this control, and nothing between the
       button and this element gets a say. */
    #onMenuPress = (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.#openMenu(event.currentTarget);
    };

    /* One ⋯ per card: the same picker the hearts use, titled for this page,
       with the remove row at the bottom. */
    #openMenu(button) {
      if (!window.customElements.get('mb-favorites-menu')) return;
      let el = document.querySelector('mb-favorites-menu');
      el ??= document.body.appendChild(document.createElement('mb-favorites-menu'));
      el.open({ id: button.dataset.productId, handle: button.dataset.productHandle },
        button, { title: 'Move to list' });
    }

    /* Each editor is a modal dialog. The store is only touched after the
       dialog has closed and handed focus back to its trigger, so the
       re-render that follows starts from a control that still exists. */
    async #createList() {
      const title = await dialog().open({
        title: 'New list',
        field: { label: 'List name', placeholder: 'e.g. Living room' },
        confirm: 'Create',
      });
      if (!title) return;
      const id = MBFavorites.createList(title);
      this.querySelector(`[data-focus-key="chip:${CSS.escape(id)}"]`)?.focus();
      this.#announce(`Created the list ${title}.`);
    }

    async #renameList() {
      const list = this.#active();
      const title = await dialog().open({
        title: 'Rename list',
        field: { label: 'List name', value: list.title },
        confirm: 'Save',
      });
      if (!title || title === list.title) return;
      this.#nextFocus = 'action:rename';
      MBFavorites.renameList(list.id, title);
      this.#announce(`List renamed to ${title}.`);
    }

    async #deleteList() {
      const list = this.#active();
      const n = list.items.length;
      const confirmed = await dialog().open({
        title: `Delete "${list.title}"?`,
        text: n === 0 ? 'This list is empty.'
            : `The ${n === 1 ? 'rug' : `${n} rugs`} saved in it will be removed from your favorites.`,
        confirm: 'Delete list',
        cancel: 'Keep',
        danger: true,
      });
      if (!confirmed) return;
      MBFavorites.deleteList(list.id);
      this.#announce(`Deleted the list ${list.title}.`);
    }

    #active() {
      return MBFavorites.lists.find((l) => l.id === MBFavorites.activeId) ?? MBFavorites.lists[0];
    }

    #announce(message) {
      const status = this.querySelector('[data-fav-status]');
      if (status) status.textContent = message;
    }

    /* ── Rendering ────────────────────────────────────────────────────── */

    #render(focusKey) {
      const lists = MBFavorites.lists;
      const active = lists.find((l) => l.id === MBFavorites.activeId) ?? lists[0];
      const wanted = focusKey ?? this.#nextFocus ?? this.#focusKey();
      this.#nextFocus = null;
      const previousIndex = this.#cardIndex(wanted);

      const total = lists.reduce((sum, l) => sum + l.items.length, 0);
      this.innerHTML = total === 0 && lists.length === 1
        ? this.#emptyState()
        : `${this.#rail(lists, active)}${this.#toolbar(lists, active)}${this.#gridShell(active)}`
          + '<p class="visually-hidden" role="status" aria-live="polite" data-fav-status></p>';

      this.#fillGrid(active);
      this.#restoreFocus(wanted, previousIndex);
      this.#ensure(active);
    }

    #focusKey() {
      return document.activeElement?.closest?.('[data-focus-key]')?.dataset.focusKey ?? null;
    }

    /* Where the control being restored sits today, so a removal can hand focus
       to whatever slides into its place. */
    #cardIndex(key) {
      const cells = [...this.querySelectorAll('[data-mb-fav-id]')];
      const cell = key?.startsWith('card:')
        ? this.querySelector(`[data-focus-key="${CSS.escape(key)}"]`)?.closest('[data-mb-fav-id]')
        : document.activeElement?.closest?.('[data-mb-fav-id]');
      return cell ? cells.indexOf(cell) : -1;
    }

    /* Focus must never land on <body> after a re-render: fall back from the
       exact control, to the card that took the removed one's place, to the
       list rail. */
    #restoreFocus(key, previousIndex) {
      if (!key) return;
      const exact = this.querySelector(`[data-focus-key="${CSS.escape(key)}"]`);
      if (exact) { exact.focus(); return; }

      if (key.startsWith('card:') && previousIndex >= 0) {
        const cells = [...this.querySelectorAll('[data-mb-fav-id]')];
        const next = cells[Math.min(previousIndex, cells.length - 1)];
        const control = next?.querySelector('[data-fav-action="menu"]');
        if (control) { control.focus(); return; }
      }

      const fallback = this.querySelector('.mb-favorites__chip.is-active')
        ?? this.querySelector('[data-focus-key^="chip:"], [data-fav-action="new"]');
      fallback?.focus();
    }

    /* ── Card supply ──────────────────────────────────────────────────── */

    /**
     * Two jobs, in order, each ending in a re-render the other can build on:
     * upgrade handle-only items to ids, then fetch card markup for ids we
     * have never rendered. Both are no-ops once the page has settled, so the
     * render → ensure → render cycle terminates.
     */
    async #ensure(active) {
      if (this.#busy) return;
      this.#busy = true;
      try {
        const stale = active.items.filter((i) => i.id === null && i.handle && !resolved.has(i.handle));
        if (stale.length) await backfill(stale);

        // Re-read the list: backfill commits, and the render that fires off
        // its event is swallowed by the lock above, so this run has to carry
        // the newly-arrived ids through itself.
        const current = this.#active();
        const missing = current.items.map((i) => i.id).filter((id) => id !== null && !cards.has(id));

        // Only re-render for work that actually landed. A failed or aborted
        // request leaves the skeletons up and waits for the next real change
        // rather than retrying itself into a spin.
        const added = missing.length ? await this.#fetchCards(missing) : 0;
        if ((added || stale.length) && this.isConnected) this.#render();
      } finally {
        this.#busy = false;
      }
    }

    /** One request per 50 ids — the section's paginate ceiling. Returns how
        many ids it resolved, so the caller knows whether to re-render. */
    async #fetchCards(ids) {
      this.#abort?.abort();
      this.#abort = new AbortController();
      const { signal } = this.#abort;
      const before = cards.size;
      const query = encodeURIComponent(ids.map((id) => `id:${id}`).join(' OR '));
      const pages = Math.ceil(ids.length / PAGE_SIZE);

      try {
        await Promise.all(Array.from({ length: pages }, async (_, i) => {
          const url = `${this.#searchUrl}?type=product&q=${query}`
                    + `&section_id=${SECTION_ID}${i ? `&page=${i + 1}` : ''}`;
          const res = await fetch(url, { signal });
          if (!res.ok) return;
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          doc.querySelectorAll('[data-mb-fav-payload] > [data-mb-fav-id]').forEach((cell) => {
            cards.set(Number(cell.dataset.mbFavId), this.#withMenu(cell));
          });
        }));
        // Anything we asked for and did not get back is gone from the catalogue.
        ids.forEach((id) => { if (!cards.has(id)) cards.set(id, null); });
      } catch {
        /* aborted or offline — skeletons stay and the next change retries */
      }
      return cards.size - before;
    }

    /* The shared card snippet knows nothing about this page, so its one
       page-specific control is added here rather than by forking the card. */
    #withMenu(cell) {
      const id = cell.dataset.mbFavId;
      // The card carries the handle; passing it on keeps the stored item whole
      // when the menu moves this rug to another list.
      const handle = cell.querySelector('[data-handle]')?.dataset.handle ?? '';
      const title = cell.querySelector('.mb-colcard__title')?.textContent.trim() ?? 'this rug';
      cell.querySelector('.mb-colcard__media-wrap')?.insertAdjacentHTML('beforeend', `
        <button type="button" class="mb-favorites__menu-btn"
          data-fav-action="menu" data-product-id="${esc(id)}" data-product-handle="${esc(handle)}"
          data-focus-key="card:${esc(id)}"
          aria-haspopup="menu" aria-expanded="false"
          aria-label="Options for ${esc(title)}">${DOTS}</button>`);
      return cell;
    }

    /* ── Markup ───────────────────────────────────────────────────────── */

    #rail(lists, active) {
      const chips = lists.map((list) => `
        <button type="button" class="mb-favorites__chip${list.id === active.id ? ' is-active' : ''}"
                data-list-id="${esc(list.id)}" data-focus-key="chip:${esc(list.id)}"
                ${list.id === active.id ? 'aria-current="true"' : ''}>
          <span class="mb-favorites__chip-name">${esc(list.title)}</span>
          <span class="mb-favorites__chip-count">${list.items.length}</span>
        </button>`).join('');

      return `<div class="mb-favorites__rail">
        <nav class="mb-favorites__chips" aria-label="Your lists">${chips}</nav>
        <button type="button" class="mb-favorites__new" data-fav-action="new" data-focus-key="action:new">
          <span aria-hidden="true">+</span> New list
        </button>
      </div>`;
    }

    #toolbar(lists, active) {
      const count = active.items.length;
      return `<div class="mb-favorites__toolbar">
        <div>
          <h2 class="mb-favorites__list-title">${esc(active.title)}</h2>
          <p class="mb-favorites__list-meta">${count} ${count === 1 ? 'item' : 'items'}</p>
        </div>
        <div class="mb-favorites__actions">
          <button type="button" class="mb-favorites__btn" data-fav-action="rename" data-focus-key="action:rename">Rename</button>
          ${lists.length > 1
            ? '<button type="button" class="mb-favorites__btn" data-fav-action="delete" data-focus-key="action:delete">Delete</button>'
            : ''}
        </div>
      </div>`;
    }

    #gridShell(active) {
      if (!active.items.length) {
        return `<div class="mb-favorites__list-empty">
          <p>Nothing saved in this list yet.</p>
          <a class="mb-favorites__cta" href="${esc(this.#browseUrl)}">Browse rugs</a>
        </div>`;
      }
      return '<div class="mb-collection__grid mb-favorites__grid" data-mb-fav-slot></div>';
    }

    /** Cached cards are cloned in; everything still in flight gets a skeleton. */
    #fillGrid(active) {
      const slot = this.querySelector('[data-mb-fav-slot]');
      if (!slot) return;
      const frag = document.createDocumentFragment();
      active.items.forEach((item) => {
        const cell = item.id !== null ? cards.get(item.id) : undefined;
        if (cell === null) return;              // gone from the catalogue
        if (!cell) { frag.appendChild(this.#skeleton()); return; }

        // cloneNode does not carry listeners, so every clone gets its own.
        const copy = cell.cloneNode(true);
        copy.querySelector('[data-fav-action="menu"]')
          ?.addEventListener('click', this.#onMenuPress);
        frag.appendChild(copy);
      });
      slot.replaceChildren(frag);
    }

    #skeleton() {
      const el = document.createElement('div');
      el.className = 'mb-favorites__card mb-favorites__skeleton';
      el.setAttribute('aria-hidden', 'true');
      el.innerHTML = '<div class="mb-favorites__skeleton-media"></div>'
        + '<div class="mb-favorites__skeleton-line"></div>'
        + '<div class="mb-favorites__skeleton-line mb-favorites__skeleton-line--short"></div>';
      return el;
    }

    #emptyState() {
      return `<div class="mb-favorites__empty">
        <span class="mb-favorites__empty-icon" aria-hidden="true">${HEART}</span>
        <h2 class="mb-favorites__empty-title">Nothing saved yet</h2>
        <p class="mb-favorites__empty-text">Tap the heart on any rug to keep it here while you decide.</p>
        <a class="mb-favorites__cta" href="${esc(this.#browseUrl)}">Browse rugs</a>
      </div>`;
    }
  }

  /* One modal for every editor on the page. Native <dialog> + showModal()
     supplies the focus trap, Escape, the inert page behind and the return of
     focus on close; this only fills the content and reports the answer. */
  class MBFavoritesDialog extends HTMLElement {
    #dialog = null;
    #resolve = null;
    #hasField = false;

    connectedCallback() {
      this.innerHTML = `
        <dialog class="mb-fav-dialog" aria-labelledby="mb-fav-dialog-title" aria-describedby="mb-fav-dialog-text">
          <form method="dialog" class="mb-fav-dialog__form" novalidate>
            <h2 class="mb-fav-dialog__title" id="mb-fav-dialog-title"></h2>
            <p class="mb-fav-dialog__text" id="mb-fav-dialog-text" hidden></p>
            <div class="mb-fav-dialog__field" hidden>
              <label class="visually-hidden" for="mb-fav-dialog-input"></label>
              <input id="mb-fav-dialog-input" class="mb-fav-dialog__input" type="text" maxlength="60" autocomplete="off">
            </div>
            <div class="mb-fav-dialog__actions">
              <button type="button" class="mb-fav-dialog__btn" value="cancel" data-fav-dialog="cancel"></button>
              <button type="submit" class="mb-fav-dialog__btn mb-fav-dialog__btn--solid" value="confirm" data-fav-dialog="confirm"></button>
            </div>
          </form>
        </dialog>`;
      this.#dialog = this.querySelector('dialog');
      this.#dialog.addEventListener('close', this.#onClose);
      this.querySelector('form').addEventListener('submit', this.#onSubmit);
      this.querySelector('[data-fav-dialog="cancel"]').addEventListener('click', () => this.#dialog.close('cancel'));
    }

    /** Resolves with the field text (or true without a field); null when dismissed. */
    open({ title, text = '', field = null, confirm = 'OK', cancel = 'Cancel', danger = false }) {
      const q = (sel) => this.querySelector(sel);
      q('.mb-fav-dialog__title').textContent = title;
      q('.mb-fav-dialog__text').textContent = text;
      q('.mb-fav-dialog__text').hidden = !text;
      this.#hasField = Boolean(field);
      q('.mb-fav-dialog__field').hidden = !field;
      q('label').textContent = field?.label ?? '';
      const input = q('input');
      input.value = field?.value ?? '';
      input.placeholder = field?.placeholder ?? '';
      q('[data-fav-dialog="cancel"]').textContent = cancel;
      q('[data-fav-dialog="confirm"]').textContent = confirm;
      q('[data-fav-dialog="confirm"]').classList.toggle('mb-fav-dialog__btn--danger', danger);

      return new Promise((resolve) => {
        this.#resolve = resolve;
        this.#dialog.returnValue = '';
        this.#dialog.showModal();
        // A destructive dialog opens on the safe answer; an editor on its field.
        if (field) { input.focus(); input.select(); }
        else (danger ? q('[data-fav-dialog="cancel"]') : q('[data-fav-dialog="confirm"]')).focus();
      });
    }

    #onSubmit = (event) => {
      if (!this.#hasField) return;
      const input = this.querySelector('input');
      if (input.value.trim()) return;
      event.preventDefault();
      input.focus();
    };

    #onClose = () => {
      const confirmed = this.#dialog.returnValue === 'confirm';
      const value = this.#hasField ? this.querySelector('input').value.trim() : true;
      this.#resolve?.(confirmed ? value : null);
      this.#resolve = null;
    };
  }

  customElements.define('mb-favorites-page', MBFavoritesPage);
  customElements.define('mb-favorites-dialog', MBFavoritesDialog);

  let dialogEl = null;
  const dialog = () => (dialogEl ??= document.body.appendChild(document.createElement('mb-favorites-dialog')));
}
